/**
 * client/ui/menus.ts — front-end screens in the BO2 LUI look: main menu, lobby, Hijacked
 * loading screen, pause, options (every Settings field), end of match.
 *
 * OWNER: HUD-UI-AUDIO agent.
 *
 * - Screens are DOM on a virtual 1280x720 stage scaled to the window (same maths as the HUD,
 *   hud/layout.ts), so every coordinate below is in LUI units.
 * - Buttons follow CoD.CoD9Button (ui/t6/cod9button.lua): Condensed 30 px labels, offWhite; the
 *   focused button turns BOIIOrange and pulses alpha 1 <-> 0.5 every 650 ms (button_over,
 *   pulse_high / pulse_low); disabled = alpha 0.5.
 * - Loading screen follows ui_mp/t6/hud/loading.lua (see showLoading).
 * - Text is drawn with the BO2 bitmap fonts (ui/bmtext.ts).
 * - Navigation: mouse, keyboard (arrows / Enter / Esc) and gamepad (D-pad or left stick,
 *   Cross = select, Circle = back, left/right change values), polled only while a menu is up.
 * - Menus only emit MenuActions; the app does the rest. Element ids #m-solo, #m-bots, #m-create,
 *   #m-code, #m-join, #m-start, #m-leave, #m-resume are used by tests/e2e/skeleton.spec.ts.
 */
import type { RoomSettings, RoomState, ServerControl } from '@shared/protocol/control';
import type { ScoreRow } from '@shared/types';
import { computeViewport } from '../hud/layout';
import { matchStore } from '../hud/matchstore';
import { WAGER_DISPLAY_ANTE, placesOf, sortPlacement, wagerPayouts } from '../hud/wager';
import { type KbAction, type Settings, defaultSettings, loadSettings } from '../settings/settings';
import { Pad } from '../input/layouts';
import { type GamepadLike, type PadRawState, createPadRawState, createPadSample, padHasInput, pickPad, readPad } from '../input/pad';
import type { MenuAction, MenuSystem, Screen } from '../types';
import { bm, bmWrapped, ensureFonts, setBm } from './bmtext';
import { OURS, STR, ordinalCaps } from './strings';

const UI = '/assets/ui/';

type Action = () => void;

interface Focusable {
  el: HTMLElement;
  activate?: Action;
  /** -1 / +1 for left/right on selectors and sliders. */
  adjust?: (dir: number) => void;
  /** Text field: Cross opens the on-screen keyboard (controller text entry). */
  osk?: OskSpec;
}

/** On-screen keyboard target. */
interface OskSpec { input: HTMLInputElement; charset: string; maxLength: number; onDone?: () => void }

/** Room code letters (host codes are A-Z) and player-name characters. */
const OSK_CODE = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const OSK_NAME = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_. ';
const OSK_COLS = 10;

/** Menu pad buttons (indices into MenuPadState). */
export const MP = { SELECT: 0, BACK: 1, UP: 2, DOWN: 3, LEFT: 4, RIGHT: 5, OPTIONS: 6, SQUARE: 7 } as const;

/**
 * Menu buttons from one pad, read with the input layer's mapping (pad.ts readPad: standard mapping or the
 * raw DualSense HID order fallback [CTL §8.1]) - Cross selects, Circle goes back on every mapping.
 */
export function menuPadButtons(s: { buttons: ArrayLike<number>; lx: number; ly: number }): boolean[] {
  const b = (i: number) => (s.buttons[i] ?? 0) > 0.5;
  return [
    b(Pad.CROSS), b(Pad.CIRCLE), b(Pad.DPAD_UP) || s.ly < -0.6, b(Pad.DPAD_DOWN) || s.ly > 0.6,
    b(Pad.DPAD_LEFT) || s.lx < -0.6, b(Pad.DPAD_RIGHT) || s.lx > 0.6, b(Pad.OPTIONS), b(Pad.SQUARE),
  ];
}

/** Share link: the room code, plus the server password when this page was opened with one (DEPLOY.md §3). */
export function shareLink(origin: string, code: string, search: string): string {
  const q = new URLSearchParams(search);
  const pw = q.get('password') ?? q.get('pw');
  const out = new URLSearchParams({ room: code });
  if (pw) out.set('password', pw);
  return `${origin}/?${out.toString()}`;
}

/** Player-facing text for a connection error code (shared/protocol/control.ts ErrorCode). */
export function netErrorText(code: string | undefined, room: string | null): string {
  switch (code) {
    case 'ROOM_NOT_FOUND': return room ? `Room ${room} was not found. The code may be wrong, or the match ended or the server restarted.` : 'The room was not found.';
    case 'ROOM_FULL': return 'The room is full.';
    case 'BAD_PASSWORD': return 'This server needs a password. Enter it below and try again.';
    case 'VERSION': return 'Game version mismatch. Reload the page to get the latest version.';
    case 'IN_PROGRESS': return 'The match has already started and cannot be joined.';
    case 'SHUTDOWN': return 'The server is shutting down.';
    default: return 'The connection to the server was lost.';
  }
}

/** Lobby time-limit choices in minutes (sas registertimelimit(0, 1440), default 5 [SNS §1]; list ESTIMATE). */
const TIME_LIMITS = [1, 2, 3, 4, 5, 10, 15, 20, 30];
const DIFFS = ['recruit', 'regular', 'hardened', 'veteran'] as const;

/** Options rows: how each Settings field is edited. */
type FieldKind =
  | { kind: 'bool'; on?: string; off?: string }
  | { kind: 'choice'; options: readonly { v: string; label: string }[] }
  | { kind: 'range'; min: number; max: number; step: number; fmt?: (v: number) => string }
  | { kind: 'text'; maxLength: number }
  | { kind: 'keybinds' };

interface FieldDef { key: string; label: string; section: string; field: FieldKind }

// Sensitivity slots 1..14 -> CoD.SENSITIVITY_n (research/data/controller.json sensitivity_table)
const SENS_TABLE = [0.4, 0.6, 0.8, 1.0, 1.2, 1.4, 1.6, 1.8, 2.0, 2.25, 2.5, 3.0, 3.5, 4.0];

/** BO2 button layouts (tools/src/bo2-raw/buttons_*.cfg via research/data/controller.json). */
export const BUTTON_LAYOUT_TABLE: Record<string, Record<string, string>> = {
  default: { R2: '+attack', L2: '+speed_throw', R1: '+frag', L1: '+smoke', R3: '+melee', L3: '+breath_sprint', CROSS: '+gostand', CIRCLE: '+stance', SQUARE: '+usereload', TRIANGLE: '+weapnext_inventory' },
  tactical: { R2: '+attack', L2: '+speed_throw', R1: '+frag', L1: '+smoke', R3: '+stance', L3: '+breath_sprint', CROSS: '+gostand', CIRCLE: '+melee', SQUARE: '+usereload', TRIANGLE: '+weapnext_inventory' },
  lefty: { R2: '+speed_throw', L2: '+attack', R1: '+smoke', L1: '+frag', R3: '+breath_sprint', L3: '+melee', CROSS: '+gostand', CIRCLE: '+stance', SQUARE: '+usereload', TRIANGLE: '+weapnext_inventory' },
  nomad: { R2: '+attack', L2: '+toggleads_throw', R1: '+frag', L1: '+smoke', R3: '+stance', L3: '+breath_sprint', CROSS: '+gostand', CIRCLE: '+melee', SQUARE: '+usereload', TRIANGLE: '+weapnext_inventory' },
  charlie: { R2: '+attack', L2: '+frag', R1: '+toggleads_throw', L1: '+smoke', R3: '+melee', L3: '+breath_sprint', CROSS: '+gostand', CIRCLE: '+stance', SQUARE: '+usereload', TRIANGLE: '+weapnext_inventory' },
};
/** thumbstick_*.cfg: which stick moves / looks. */
export const STICK_LAYOUT_TABLE: Record<string, { move: 'L' | 'R' | 'mixed'; desc: string }> = {
  default: { move: 'L', desc: 'L: MOVE   R: LOOK' },
  southpaw: { move: 'R', desc: 'L: LOOK   R: MOVE' },
  legacy: { move: 'mixed', desc: 'L: FORWARD + TURN   R: STRAFE + LOOK' },
  legacysouthpaw: { move: 'mixed', desc: 'L: STRAFE + LOOK   R: FORWARD + TURN' },
};
const COMMAND_LABELS: Record<string, string> = {
  '+attack': 'FIRE', '+speed_throw': 'AIM DOWN SIGHT', '+toggleads_throw': 'AIM DOWN SIGHT (TOGGLE)', '+frag': 'LETHAL',
  '+smoke': 'TACTICAL', '+melee': 'MELEE', '+breath_sprint': 'SPRINT / HOLD BREATH', '+gostand': 'JUMP',
  '+stance': 'CROUCH / PRONE', '+usereload': 'USE / RELOAD', '+weapnext_inventory': 'SWITCH WEAPON',
};
const PS_BUTTONS: Record<string, string> = {
  R2: 'R2', L2: 'L2', R1: 'R1', L1: 'L1', R3: 'R3', L3: 'L3', CROSS: '✕', CIRCLE: '○', SQUARE: '□', TRIANGLE: '△',
};

const onOff = (on: string = STR.MENU_ENABLED_CAPS, off: string = STR.MENU_DISABLED_CAPS): FieldKind => ({ kind: 'bool', on, off });
const pct = (v: number) => `${Math.round(v * 100)}`;

/** Known Settings fields (others are discovered from defaultSettings() and edited generically). */
const FIELD_DEFS: FieldDef[] = [
  { key: 'name', label: OURS.NAME, section: 'PLAYER', field: { kind: 'text', maxLength: 16 } },
  // ---- controller (BO2 "Controls" menu) -----------------------------------------------------
  { key: 'lookSensitivity', label: STR.MENU_LOOK_SENSITIVITY_CAPS, section: STR.MENU_CONTROLS_CAPS, field: { kind: 'range', min: 1, max: 14, step: 1, fmt: (v) => String(v) } },
  { key: 'invertPitch', label: STR.MENU_LOOK_INVERSION_CAPS, section: STR.MENU_CONTROLS_CAPS, field: onOff() },
  {
    key: 'buttonLayout', label: STR.MENU_BUTTON_LAYOUT_CAPS, section: STR.MENU_CONTROLS_CAPS,
    field: { kind: 'choice', options: [
      { v: 'default', label: STR.MENU_DEFAULT_CAPS }, { v: 'tactical', label: STR.MENU_TACTICAL_CAPS }, { v: 'lefty', label: STR.MENU_LEFTY_CAPS },
      { v: 'nomad', label: OURS.NOMAD }, { v: 'charlie', label: STR.MENU_CHARLIE_CAPS },
    ] },
  },
  { key: 'triggersFlipped', label: OURS.TRIGGERS_FLIPPED, section: STR.MENU_CONTROLS_CAPS, field: onOff(STR.MENU_FLIPPED_CAPS, STR.MENU_DEFAULT_CAPS) },
  {
    key: 'stickLayout', label: STR.MENU_STICK_LAYOUT_CAPS, section: STR.MENU_CONTROLS_CAPS,
    field: { kind: 'choice', options: [
      { v: 'default', label: STR.MENU_DEFAULT_CAPS }, { v: 'southpaw', label: STR.MENU_SOUTHPAW_CAPS },
      { v: 'legacy', label: STR.MENU_LEGACY_CAPS }, { v: 'legacysouthpaw', label: STR.MENU_LEGACY_SOUTHPAW_CAPS },
    ] },
  },
  { key: 'aimAssist', label: STR.MENU_TARGET_ASSIST_CAPS, section: STR.MENU_CONTROLS_CAPS, field: onOff() },
  { key: 'adsAutoAim', label: STR.MENU_AIM_DOWN_THE_SIGHT_AUTOAIM.toUpperCase(), section: STR.MENU_CONTROLS_CAPS, field: onOff() },
  { key: 'aimLockOn', label: 'TARGET ASSIST LOCK-ON', section: STR.MENU_CONTROLS_CAPS, field: onOff() },
  { key: 'rumble', label: STR.MENU_CONTROLLER_VIBRATION_CAPS, section: STR.MENU_CONTROLS_CAPS, field: onOff() },
  { key: 'webHidRumble', label: 'DUALSENSE RUMBLE (WEBHID)', section: STR.MENU_CONTROLS_CAPS, field: onOff() },
  { key: 'altStickCurve', label: OURS.ALT_STICK_CURVE, section: STR.MENU_CONTROLS_CAPS, field: onOff() },
  // ---- keyboard / mouse ---------------------------------------------------------------------
  { key: 'mouseSensitivity', label: STR.MENU_MOUSE_SENSITIVITY_CAPS, section: OURS.KEYBINDS, field: { kind: 'range', min: 0.0055, max: 0.33, step: 0.0055, fmt: (v) => (v / 0.022).toFixed(2) } },
  { key: 'mouseAdsScale', label: OURS.ADS_MOUSE_SCALE, section: OURS.KEYBINDS, field: { kind: 'range', min: 0.25, max: 2, step: 0.05, fmt: (v) => v.toFixed(2) } },
  { key: 'mouseRawInput', label: 'RAW MOUSE INPUT', section: OURS.KEYBINDS, field: onOff() },
  { key: 'toggleCrouch', label: OURS.TOGGLE_CROUCH, section: OURS.KEYBINDS, field: onOff(STR.MENU_ON_CAPS, STR.MENU_OFF_CAPS) },
  { key: 'toggleAds', label: OURS.TOGGLE_ADS, section: OURS.KEYBINDS, field: onOff(STR.MENU_ON_CAPS, STR.MENU_OFF_CAPS) },
  { key: 'keybinds', label: OURS.KEYBINDS, section: OURS.KEYBINDS, field: { kind: 'keybinds' } },
  // ---- view / audio / graphics --------------------------------------------------------------
  { key: 'fov', label: STR.PLATFORM_FIELD_OF_VIEW_CAPS, section: STR.MENU_GRAPHICS_CAPS, field: { kind: 'range', min: 65, max: 90, step: 1, fmt: (v) => String(v) } },
  { key: 'renderScale', label: OURS.RENDER_SCALE, section: STR.MENU_GRAPHICS_CAPS, field: { kind: 'range', min: 0.5, max: 1, step: 0.05, fmt: (v) => `${Math.round(v * 100)}%` } },
  { key: 'shadows', label: OURS.SHADOWS, section: STR.MENU_GRAPHICS_CAPS, field: onOff(STR.MENU_ON_CAPS, STR.MENU_OFF_CAPS) },
  { key: 'volume.master', label: STR.MENU_MASTER_VOLUME_CAPS, section: STR.MENU_AUDIO_SETTINGS_CAPS, field: { kind: 'range', min: 0, max: 1, step: 0.05, fmt: pct } },
  { key: 'volume.sfx', label: STR.MENU_SFX_VOLUME_CAPS, section: STR.MENU_AUDIO_SETTINGS_CAPS, field: { kind: 'range', min: 0, max: 1, step: 0.05, fmt: pct } },
  { key: 'volume.music', label: STR.MENU_MUSIC_VOLUME_CAPS, section: STR.MENU_AUDIO_SETTINGS_CAPS, field: { kind: 'range', min: 0, max: 1, step: 0.05, fmt: pct } },
  { key: 'volume.voice', label: OURS.VOICE_VOLUME, section: STR.MENU_AUDIO_SETTINGS_CAPS, field: { kind: 'range', min: 0, max: 1, step: 0.05, fmt: pct } },
  { key: 'showDebugOverlay', label: OURS.DEBUG_OVERLAY, section: 'NETWORK', field: onOff(STR.MENU_ON_CAPS, STR.MENU_OFF_CAPS) },
  { key: 'timeNudge', label: OURS.TIME_NUDGE, section: 'NETWORK', field: { kind: 'range', min: -30, max: 30, step: 1, fmt: (v) => `${v} MS` } },
];

/** All option rows for a settings object: known fields + generic rows for any new field. */
export function optionFields(s: Settings): FieldDef[] {
  const known = new Set(FIELD_DEFS.map((f) => f.key.split('.')[0]));
  known.add('version');
  const out = FIELD_DEFS.filter((f) => getPath(s, f.key) !== undefined);
  for (const [k, v] of Object.entries(s)) {
    if (known.has(k)) continue;
    const label = k.replace(/([A-Z])/g, ' $1').toUpperCase();
    if (typeof v === 'boolean') out.push({ key: k, label, section: 'OTHER', field: onOff(STR.MENU_ON_CAPS, STR.MENU_OFF_CAPS) });
    else if (typeof v === 'number') out.push({ key: k, label, section: 'OTHER', field: { kind: 'range', min: Math.min(0, v), max: Math.max(1, v * 4), step: Math.abs(v) >= 2 ? 1 : 0.05 } });
    else if (typeof v === 'string') out.push({ key: k, label, section: 'OTHER', field: { kind: 'text', maxLength: 32 } });
  }
  return out;
}

export function getPath(o: unknown, path: string): unknown {
  let cur = o as Record<string, unknown> | undefined;
  for (const p of path.split('.')) { if (!cur || typeof cur !== 'object') return undefined; cur = cur[p] as Record<string, unknown> | undefined; }
  return cur;
}
export function setPath(o: Record<string, unknown>, path: string, v: unknown): void {
  const parts = path.split('.');
  let cur = o;
  for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]!] as Record<string, unknown>;
  cur[parts[parts.length - 1]!] = v;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, parent?: HTMLElement): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  parent?.appendChild(e);
  return e;
}
function img(src: string, cls: string, parent?: HTMLElement): HTMLImageElement {
  const i = el('img', cls, parent);
  i.src = UI + src;
  i.alt = '';
  i.draggable = false;
  return i;
}
function place(e: HTMLElement, x: number, y: number, w?: number, h?: number): HTMLElement {
  e.style.left = `${x}px`; e.style.top = `${y}px`;
  if (w !== undefined) e.style.width = `${w}px`;
  if (h !== undefined) e.style.height = `${h}px`;
  return e;
}

const LOADING_TIPS = ['MPTIP_WAGER_GOLD_DOT', 'MPTIP_WAGER_STICKS_TOMAHAWK', 'MPTIP_WAGER_STICKS_POINTS', 'MPTIP_WAGER_STICKS_SPYPLANE', 'MPTIP_ADS_TIP'] as const;

export class Bo2Menus implements MenuSystem {
  current: Screen = 'none';
  private root!: HTMLDivElement;
  private stage!: HTMLDivElement;
  private cb: (a: MenuAction) => void = () => {};
  private settings: Settings = loadSettings();
  private focusables: Focusable[] = [];
  private focusIdx = -1;
  private backAction: Action | null = null;
  private returnTo: Screen = 'main';
  private room: RoomState | null = null;
  private padRaf = 0;
  private padPrev: boolean[] = [];
  private padRepeatAt = 0;
  private padIdx = -1;
  private readonly padSample = createPadSample();
  private readonly padRaw = new Map<number, PadRawState>();
  private osk: { spec: OskSpec; keys: { el: HTMLElement; ch: string }[]; idx: number; root: HTMLElement; prevFocus: number } | null = null;
  private gestureEl: HTMLElement | null = null;
  private ready = false;
  private loadingEls: { bar: HTMLDivElement; label: HTMLSpanElement } | null = null;
  private rebinding: { action: KbAction; done: () => void } | null = null;
  private endData: { results: ScoreRow[]; winner: number | null } | null = null;
  private lastData: unknown = undefined;
  private localClientNum = -1;
  /** Virtual stage width (1280 at 16:9, wider on ultrawide). */
  private vw = 1280;

  mount(parent: HTMLElement): void {
    this.root = el('div', 'menu bo2-menu', parent);
    this.stage = el('div', 'bo2-stage', this.root);
    this.root.style.display = 'none';
    const fit = () => {
      const vp = computeViewport(this.root.clientWidth || window.innerWidth, this.root.clientHeight || window.innerHeight);
      this.vw = vp.vw;
      this.stage.style.width = `${vp.vw}px`;
      this.stage.style.height = `${vp.vh}px`;
      this.stage.style.transform = `scale(${vp.scale})`;
    };
    window.addEventListener('resize', fit);
    new ResizeObserver(fit).observe(this.root);
    fit();
    window.addEventListener('keydown', (e) => this.onKey(e), true);
    window.addEventListener('mousedown', (e) => this.onMouseRebind(e), true);
    void ensureFonts();
  }

  onAction(cb: (a: MenuAction) => void): void { this.cb = cb; }

  show(screen: Screen, data?: unknown): void {
    if (this.current === 'options' && screen === 'options') return;
    // a re-render of the same screen (lobby room updates) keeps the focused item
    const keepFocus = this.current === screen ? this.focusIdx : -1;
    this.current = screen;
    this.lastData = data;
    this.root.style.display = screen === 'none' ? 'none' : '';
    this.root.dataset['screen'] = screen;
    this.stage.replaceChildren();
    this.focusables = [];
    this.focusIdx = -1;
    this.backAction = null;
    this.loadingEls = null;
    this.rebinding = null;
    this.osk = null;
    this.gestureEl = null;
    // prime the edge detector with the buttons held right now: the Options press that opened the pause menu
    // (or the Cross that picked a menu item) must not count as a new press on the new screen
    this.padPrev = this.readMenuPad() ?? [];
    switch (screen) {
      case 'main': this.showMain(); break;
      case 'lobby': this.showLobby(data); break;
      case 'loading': this.showLoading(); break;
      case 'pause': this.showPause(); break;
      case 'options': this.showOptions(); break;
      case 'end': this.showEnd(data); break;
      case 'error': this.showError(data); break;
      default: break;
    }
    if (screen === 'main' || screen === 'lobby' || screen === 'error') this.gesturePrompt();
    if (this.focusables.length && screen !== 'loading') {
      // start on the first button / row, not on a text field (arrows would edit it)
      const first = this.focusables.findIndex((f) => !(f.el instanceof HTMLInputElement));
      this.focus(keepFocus >= 0 && keepFocus < this.focusables.length ? keepFocus : Math.max(0, first), false);
    }
    this.updatePadLoop();
  }

  setLoading(frac: number, label: string): void {
    if (!this.loadingEls) return;
    this.loadingEls.bar.style.width = `${Math.max(0, Math.min(1, frac)) * 100}%`;
    setBm(this.loadingEls.label, label.toUpperCase());
  }

  // =========================================================================================
  // shared pieces
  // =========================================================================================
  private backdrop(kind: 'front' | 'ingame'): void {
    const bg = el('div', `b2-bg b2-bg-${kind}`, this.stage);
    if (kind === 'front') {
      img('loading/loadscreen_mp_hijacked_1920x1080.jpg', 'b2-bg-img', bg);
      el('div', 'b2-bg-dim', bg);
    }
    img('menu/gradient_top.png', 'b2-grad-top', bg);
    img('menu/gradient_bottom.png', 'b2-grad-bottom', bg);
  }

  /** Lobby title (CoD.Menu title: Big font, left of the safe area) + subtitle. */
  private title(text: string, sub?: string): void {
    const t = place(el('div', 'b2-title', this.stage), 64 + 6, 36 + 18);
    t.appendChild(bm(text, 'Big', { upper: true }));
    if (sub) {
      const s = place(el('div', 'b2-subtitle', this.stage), 64 + 8, 36 + 18 + 48);
      s.appendChild(bm(sub, 'Condensed', { upper: true }));
    }
    place(el('div', 'b2-title-line', this.stage), 64, 36 + 18 + 48 + (sub ? 32 : 0) + 4, 1152, 1);
  }

  /** CoD9Button list. */
  private buttonList(x: number, y: number, items: { id?: string; label: string; act: Action; disabled?: boolean }[]): HTMLDivElement {
    const list = place(el('div', 'b2-list', this.stage), x, y) as HTMLDivElement;
    for (const it of items) {
      const b = el('button', 'b2-btn', list);
      if (it.id) b.id = it.id;
      b.type = 'button';
      b.appendChild(bm(it.label, 'Condensed', { upper: true }));
      if (it.disabled) { b.disabled = true; b.classList.add('disabled'); }
      b.addEventListener('click', () => { if (!b.disabled) it.act(); });
      this.addFocus(b, it.disabled ? undefined : it.act);
    }
    return list;
  }

  private addFocus(e: HTMLElement, activate?: Action, adjust?: (d: number) => void, osk?: OskSpec): void {
    const idx = this.focusables.length;
    this.focusables.push({ el: e, activate, adjust, osk });
    e.addEventListener('mouseenter', () => this.focus(idx, false));
    e.addEventListener('focus', () => { if (this.focusIdx !== idx) this.focus(idx, false); });
  }

  private focus(i: number, scroll = true): void {
    if (!this.focusables.length) return;
    i = (i + this.focusables.length) % this.focusables.length;
    const prev = this.focusables[this.focusIdx];
    prev?.el.classList.remove('focus');
    this.focusIdx = i;
    const f = this.focusables[i]!;
    f.el.classList.add('focus');
    if (document.activeElement !== f.el) {
      // moving focus off a text field must blur it, or the arrow keys keep editing the field
      if (document.activeElement instanceof HTMLInputElement) document.activeElement.blur();
      if (!f.el.hasAttribute('tabindex') && !(f.el instanceof HTMLButtonElement) && !(f.el instanceof HTMLInputElement)) f.el.tabIndex = -1;
      f.el.focus({ preventScroll: !scroll });
    }
    if (scroll) f.el.scrollIntoView({ block: 'nearest' });
  }

  /** Bottom button-prompt bar (PS glyphs; BO2 ships only Xbox glyphs, so these are drawn). */
  private prompts(items: [string, string][]): void {
    const bar = place(el('div', 'b2-prompts', this.stage), 64, 720 - 36 - 30);
    for (const [glyph, label] of items) {
      const p = el('span', 'b2-prompt', bar);
      el('span', `b2-glyph g-${glyph}`, p).textContent = PS_BUTTONS[glyph] ?? glyph;
      p.appendChild(bm(label, 'ExtraSmall', { upper: true }));
    }
  }

  private mapPanel(x: number, y: number): void {
    const p = place(el('div', 'b2-mappanel', this.stage), x, y, 420);
    const frame = el('div', 'b2-mapframe', p);
    img('loading/menu_mp_hijacked_map_select_final.png', 'b2-mapimg', frame);
    const cap = el('div', 'b2-mapcap', frame);
    cap.appendChild(bm(STR.MPUI_HIJACKED_CAPS, 'Condensed'));
    const mode = el('div', 'b2-mode', p);
    img('wager/playlist_sticks_and_stones.png', 'b2-modeicon', mode);
    const mt = el('div', 'b2-modetext', mode);
    mt.appendChild(bm(STR.MPUI_SAS_CAPS, 'Condensed'));
    const d = el('p', 'b2-desc', mt);
    d.textContent = STR.MENU_SAS_DESC;
    const d2 = el('p', 'b2-desc b2-desc2', p);
    d2.textContent = STR.MPUI_DESC_MAP_HIJACKED;
  }

  // =========================================================================================
  // screens
  // =========================================================================================
  private showMain(): void {
    this.backdrop('front');
    this.title(STR.MENU_PARTYGAMES_CAPS, STR.MPUI_SAS);
    let bots = 3;
    let diff = 1; // regular (bots.md §2 default)
    const list = this.buttonList(64 + 6, 170, [
      { id: 'm-solo', label: OURS.PLAY_SOLO, act: () => this.cb({ a: 'solo', bots: botsVal(), map: 'mp_hijacked', difficulty: DIFFS[diff]! }) },
      // online rooms start without bots; the leader sets bots / difficulty / time limit in the lobby (#H2)
      { id: 'm-create', label: OURS.CREATE_ROOM, act: () => this.cb({ a: 'create', map: 'mp_hijacked', bots: 0, difficulty: DIFFS[diff]! }) },
      { id: 'm-join', label: OURS.JOIN_ROOM, act: () => this.join(codeInput.value) },
      { label: STR.MENU_OPTIONS_CAPS, act: () => this.openOptions('main') },
    ]);
    // bot count selector (#m-bots must be an <input> for the skeleton test)
    const botsRow = el('div', 'b2-inline', list);
    el('span', 'b2-inline-label', botsRow).appendChild(bm(OURS.BOTS, 'ExtraSmall'));
    const minus = el('button', 'b2-arrow', botsRow); minus.type = 'button'; minus.textContent = '◀';
    const botsInput = el('input', 'b2-input b2-num', botsRow);
    botsInput.id = 'm-bots'; botsInput.type = 'number'; botsInput.min = '0'; botsInput.max = '7'; botsInput.value = String(bots);
    const plus = el('button', 'b2-arrow', botsRow); plus.type = 'button'; plus.textContent = '▶';
    const botsVal = () => Math.max(0, Math.min(7, Math.round(Number(botsInput.value) || 0)));
    const setBots = (d: number) => { bots = Math.max(0, Math.min(7, botsVal() + d)); botsInput.value = String(bots); };
    minus.addEventListener('click', () => setBots(-1));
    plus.addEventListener('click', () => setBots(1));
    this.addFocus(botsInput, undefined, setBots);
    // (integration, #B3 / #H1.5) bot difficulty: Recruit / Regular / Hardened / Veteran [research/bots.md §2]
    const diffRow = el('div', 'b2-inline', list);
    el('span', 'b2-inline-label', diffRow).appendChild(bm('BOT DIFFICULTY', 'ExtraSmall'));
    const dl = el('button', 'b2-arrow', diffRow); dl.type = 'button'; dl.textContent = '◀';
    const dtxt = bm('', 'Default'); dtxt.id = 'm-difficulty'; diffRow.appendChild(dtxt);
    const dr = el('button', 'b2-arrow', diffRow); dr.type = 'button'; dr.textContent = '▶';
    diffRow.insertBefore(dtxt, dr);
    const setDiff = (d: number) => { diff = (diff + d + DIFFS.length) % DIFFS.length; setBm(dtxt, DIFFS[diff]!.toUpperCase(), true); };
    setDiff(0);
    dl.addEventListener('click', () => setDiff(-1));
    dr.addEventListener('click', () => setDiff(1));
    this.addFocus(diffRow, () => setDiff(1), setDiff);
    // room code
    const codeRow = el('div', 'b2-inline', list);
    el('span', 'b2-inline-label', codeRow).appendChild(bm(OURS.ROOM_CODE, 'ExtraSmall'));
    const codeInput = el('input', 'b2-input b2-code', codeRow);
    codeInput.id = 'm-code'; codeInput.maxLength = 4; codeInput.placeholder = 'ABCD'; codeInput.autocomplete = 'off';
    codeInput.addEventListener('input', () => { codeInput.value = codeInput.value.toUpperCase().replace(/[^A-Z]/g, ''); });
    codeInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') this.join(codeInput.value); });
    this.addFocus(codeInput, undefined, undefined, { input: codeInput, charset: OSK_CODE, maxLength: 4, onDone: () => this.join(codeInput.value) });
    // player name
    const nameRow = el('div', 'b2-inline', list);
    el('span', 'b2-inline-label', nameRow).appendChild(bm(OURS.NAME, 'ExtraSmall'));
    const nameInput = el('input', 'b2-input b2-name', nameRow);
    nameInput.maxLength = 16; nameInput.value = this.settings.name; nameInput.autocomplete = 'off';
    nameInput.addEventListener('change', () => this.setSetting('name', nameInput.value.trim() || 'Player'));
    this.addFocus(nameInput, undefined, undefined, { input: nameInput, charset: OSK_NAME, maxLength: 16, onDone: () => this.setSetting('name', nameInput.value.trim() || 'Player') });

    this.mapPanel(1280 - 64 - 420, 150);
    this.prompts([['CROSS', 'Select'], ['CIRCLE', 'Back']]);
    const hint = place(el('div', 'b2-hint', this.stage), 64 + 6, 720 - 36 - 60);
    hint.textContent = 'Click the game to lock the mouse. Esc releases it and pauses.';
  }

  private join(code: string): void {
    const c = code.toUpperCase().replace(/[^A-Z]/g, '');
    if (c.length === 4) this.cb({ a: 'join', code: c });
  }

  /** data: RoomState, or { room, localClientNum } (CONTRACT-REQUEST #H1.4), or null while connecting. */
  private showLobby(data: unknown): void {
    let room: RoomState | null | undefined = data as RoomState | null | undefined;
    if (data && typeof data === 'object' && 'room' in data) {
      const d = data as { room: RoomState | null; localClientNum?: number };
      room = d.room;
      if (typeof d.localClientNum === 'number') this.localClientNum = d.localClientNum;
    }
    if (room !== undefined) this.room = room;
    const r = room ?? null;
    if (!r) this.ready = false;
    const me = r?.players.find((p) => p.clientNum === this.localClientNum);
    if (me) this.ready = me.ready;
    const isLeader = !!r && this.localClientNum >= 0 && r.leader === this.localClientNum;
    const notLeader = !!r && this.localClientNum >= 0 && r.leader !== this.localClientNum;
    this.backdrop('front');
    this.title(STR.MENU_PARTYGAMES_CAPS, STR.MENU_LOBBY_CAPS);
    const items: { id?: string; label: string; act: Action; disabled?: boolean }[] = [
      // connecting (no room yet): nothing to start
      { id: 'm-start', label: STR.MPUI_START_MATCH_CAPS, act: () => this.cb({ a: 'start' }), disabled: notLeader || !r },
    ];
    // ready toggle for the other players (BO2 private matches have no ready-up; the host shows it as a tag)
    if (notLeader) {
      items.push({ id: 'm-ready', label: this.ready ? 'CANCEL READY' : 'READY', act: () => { this.ready = !this.ready; this.cb({ a: 'ready', ready: this.ready }); } });
    }
    items.push(
      { label: STR.MENU_OPTIONS_CAPS, act: () => this.openOptions('lobby') },
      { id: 'm-leave', label: STR.MENU_LEAVE_GAME_CAPS, act: () => this.cb({ a: 'leave' }) },
    );
    const list = this.buttonList(64 + 6, 170, items);
    this.backAction = () => this.cb({ a: 'leave' });
    // leader-only match settings: bots, bot difficulty, time limit (host 'settings' message)
    if (r && isLeader) this.lobbySettings(list, r.settings);
    // room code + share link
    const info = place(el('div', 'b2-roominfo', this.stage), 64 + 6, isLeader ? 400 : 360, 420);
    info.appendChild(bm(OURS.ROOM_CODE, 'ExtraSmall'));
    const code = el('div', 'b2-roomcode', info);
    code.appendChild(bm(r?.code ?? '....', 'Big'));
    info.appendChild(bm(OURS.SHARE_LINK, 'ExtraSmall'));
    const link = el('div', 'b2-link', info);
    const url = r ? shareLink(location.origin, r.code, location.search) : '';
    link.textContent = url || OURS.CONNECTING;
    const copy = el('button', 'b2-btn b2-small', info);
    copy.type = 'button';
    copy.appendChild(bm(OURS.COPY_LINK, 'ExtraSmall'));
    const doCopy = () => { if (url) void navigator.clipboard?.writeText(url).catch(() => {}); };
    copy.addEventListener('click', doCopy);
    this.addFocus(copy, doCopy);
    // players
    const plist = place(el('div', 'b2-players', this.stage), 1280 - 64 - 420, 150, 420);
    const head = el('div', 'b2-players-head', plist);
    head.appendChild(bm(`${STR.MENU_PLAYERS_CAPS}  ${r?.players.length ?? 0}/8`, 'Condensed'));
    for (const p of r?.players ?? []) {
      const row = el('div', `b2-prow${p.clientNum === r?.leader ? ' leader' : ''}${p.ready ? ' ready' : ''}`, plist);
      row.appendChild(bm(p.name, 'Default'));
      const tag = el('span', 'b2-ptag', row);
      tag.appendChild(bm(p.clientNum === r?.leader ? STR.MENU_HOST_CAPS : p.isBot ? 'BOT' : p.ready ? 'READY' : '', 'ExtraSmall'));
    }
    if (r) {
      const s = r.settings;
      const rules = el('div', 'b2-rules', plist);
      rules.appendChild(bm(`${STR.MPUI_SAS_CAPS} - ${STR.MPUI_HIJACKED_CAPS}`, 'ExtraSmall'));
      rules.appendChild(bm(`${STR.MPUI_RULES_TIME_LIMIT_CAPS}: ${s.timeLimitMin}:00    BOTS: ${s.bots} (${s.botDifficulty.toUpperCase()})`, 'ExtraSmall'));
    }
    this.prompts([['CROSS', 'Select'], ['CIRCLE', 'Leave']]);
  }

  /** Leader's lobby selectors; each change sends the host a partial RoomSettings. */
  private lobbySettings(list: HTMLElement, s: RoomSettings): void {
    const send = (settings: Partial<RoomSettings>) => this.cb({ a: 'roomSettings', settings });
    const selector = (id: string, label: string, text: string, step: (d: number) => void) => {
      const row = el('div', 'b2-inline', list);
      row.id = id;
      el('span', 'b2-inline-label', row).appendChild(bm(label, 'ExtraSmall'));
      const l = el('button', 'b2-arrow', row); l.type = 'button'; l.textContent = '◀';
      el('span', 'b2-sel-val', row).appendChild(bm(text, 'Default'));
      const rr = el('button', 'b2-arrow', row); rr.type = 'button'; rr.textContent = '▶';
      l.addEventListener('click', () => step(-1));
      rr.addEventListener('click', () => step(1));
      this.addFocus(row, () => step(1), step);
    };
    selector('m-lobby-bots', OURS.BOTS, String(s.bots), (d) => send({ bots: Math.max(0, Math.min(7, s.bots + d)) }));
    const di = Math.max(0, DIFFS.indexOf(s.botDifficulty));
    selector('m-lobby-diff', 'BOT DIFFICULTY', s.botDifficulty.toUpperCase(), (d) => send({ botDifficulty: DIFFS[(di + d + DIFFS.length) % DIFFS.length]! }));
    const ti = TIME_LIMITS.indexOf(s.timeLimitMin);
    selector('m-lobby-time', STR.MPUI_RULES_TIME_LIMIT_CAPS, `${s.timeLimitMin}:00`, (d) => {
      const i = ti < 0 ? TIME_LIMITS.indexOf(5) : ti;
      send({ timeLimitMin: TIME_LIMITS[Math.max(0, Math.min(TIME_LIMITS.length - 1, i + d))]! });
    });
  }

  /** Connection error / closed session: message + a way back to the main menu (and a password retry). */
  private showError(data: unknown): void {
    const d = (data ?? {}) as { code?: string; msg?: string; room?: string | null };
    this.backdrop('front');
    this.title(STR.MENU_PARTYGAMES_CAPS, 'CONNECTION ERROR');
    const box = place(el('div', 'b2-error', this.stage), 64 + 6, 170, 640);
    box.id = 'm-error';
    box.dataset['code'] = d.code ?? '';
    el('p', 'b2-error-text', box).textContent = netErrorText(d.code, d.room ?? null);
    if (d.msg) el('p', 'b2-error-detail', box).textContent = d.code ? `${d.code}: ${d.msg}` : d.msg;
    const items: { id?: string; label: string; act: Action }[] = [];
    if (d.code === 'BAD_PASSWORD') {
      const row = el('div', 'b2-inline', box);
      el('span', 'b2-inline-label', row).appendChild(bm('PASSWORD', 'ExtraSmall'));
      const pw = el('input', 'b2-input b2-name', row);
      pw.id = 'm-password'; pw.type = 'password'; pw.maxLength = 64; pw.autocomplete = 'off';
      const retry = () => {
        if (!pw.value) return;
        const q = new URLSearchParams(location.search);
        // with a room code: rejoin it; without (creating a room): back to the menu with the password set
        q.delete('pw'); if (d.room) q.set('room', d.room); q.set('password', pw.value);
        location.assign(`${location.pathname}?${q.toString()}`);
      };
      pw.addEventListener('keydown', (e) => { if (e.key === 'Enter') retry(); });
      this.addFocus(pw, undefined, undefined, { input: pw, charset: OSK_NAME, maxLength: 64, onDone: retry });
      items.push({ id: 'm-retry', label: 'TRY AGAIN', act: retry });
    }
    items.push({ id: 'm-error-back', label: STR.MENU_BACK_CAPS, act: () => this.cb({ a: 'leave' }) });
    this.buttonList(64 + 6, 380, items);
    this.backAction = () => this.cb({ a: 'leave' });
    this.prompts([['CROSS', 'Select'], ['CIRCLE', 'Back']]);
  }

  /** ui_mp/t6/hud/loading.lua. */
  private showLoading(): void {
    const root = el('div', 'b2-loading', this.stage);
    // map image: starts black (RGB 0), fades to full after the three text fades (2 s delay + 3 x 1 s)
    img('loading/loadscreen_mp_hijacked_1920x1080.jpg', 'b2-load-img', root);
    img('menu/gradient_top.png', 'b2-grad-top', root);
    img('menu/gradient_bottom.png', 'b2-grad-bottom', root);
    // map name (Big, BOIIOrange) at (70,50); location (Condensed, offWhite) at 93; gametype at 121 alpha 0.6
    place(el('div', 'b2-load-t b2-load-name', root), 70, 50).appendChild(bm(STR.MPUI_HIJACKED_CAPS, 'Big'));
    place(el('div', 'b2-load-t b2-load-loc', root), 70, 93).appendChild(bm(STR.MPUI_HIJACKED_LOC, 'Condensed'));
    place(el('div', 'b2-load-t b2-load-gt', root), 70, 121).appendChild(bm(STR.MPUI_SAS_CAPS, 'Condensed'));
    // did-you-know + loading bar: 854 wide, centred, bottom at -200 (ESTIMATE: garbled decompile)
    const box = place(el('div', 'b2-load-box', root), this.vw / 2 - 427, 720 - 200 - 31 - 1 - 2 - 25, 854);
    const dyk = el('div', 'b2-dyk', box);
    const tipKey = LOADING_TIPS[Math.floor(Math.random() * LOADING_TIPS.length)]!;
    const tip = el('div', 'b2-dyk-text', dyk);
    tip.appendChild(bmWrapped(STR[tipKey], 'Default', 854 - 9 - 36));
    img('loading/lui_loader_32.png', 'b2-spinner', dyk);
    const track = el('div', 'b2-load-track', box);
    const bar = el('div', 'b2-load-bar', track);
    const lab = el('div', 'b2-load-label', box);
    const label = bm(`${STR.EXE_LOADING.toUpperCase()}`, 'ExtraSmall');
    lab.appendChild(label);
    this.loadingEls = { bar, label };
  }

  private showPause(): void {
    this.backdrop('ingame');
    this.title(STR.MPUI_SAS_CAPS, STR.MPUI_HIJACKED);
    this.buttonList(64 + 6, 170, [
      { id: 'm-resume', label: STR.MENU_RESUMEGAME_CAPS, act: () => this.cb({ a: 'resume' }) },
      { label: STR.MENU_OPTIONS_CAPS, act: () => this.openOptions('pause') },
      { id: 'm-leave', label: STR.MENU_LEAVE_GAME_CAPS, act: () => this.cb({ a: 'leave' }) },
    ]);
    this.backAction = () => this.cb({ a: 'resume' });
    const obj = place(el('div', 'b2-objective', this.stage), 64 + 6, 330, 500);
    obj.appendChild(bm(STR.OBJECTIVES_SAS_HINT, 'Condensed'));
    el('p', 'b2-desc', obj).textContent = STR.OBJECTIVES_SAS_SCORE;
    this.prompts([['CROSS', 'Select'], ['CIRCLE', 'Resume']]);
  }

  private openOptions(from: Screen): void {
    this.returnTo = from;
    this.settings = loadSettings();
    this.show('options');
  }

  private showOptions(): void {
    this.backdrop(this.returnTo === 'pause' ? 'ingame' : 'front');
    this.title(STR.MENU_OPTIONS_CAPS, STR.MENU_SETTINGS_CAPS);
    const back = () => { const to = this.returnTo; this.show(to, to === 'lobby' ? this.room : undefined); };
    this.backAction = back;
    const scroller = place(el('div', 'b2-options', this.stage), 64 + 6, 160, 700, 470);
    let section = '';
    for (const f of optionFields(this.settings)) {
      if (f.section !== section) {
        section = f.section;
        el('div', 'b2-opt-section', scroller).appendChild(bm(section, 'ExtraSmall'));
      }
      this.optionRow(scroller, f);
    }
    const backBtn = el('button', 'b2-btn b2-back', scroller);
    backBtn.type = 'button'; backBtn.id = 'm-options-back';
    backBtn.appendChild(bm(STR.MENU_BACK_CAPS, 'Condensed'));
    backBtn.addEventListener('click', back);
    this.addFocus(backBtn, back);
    // layout diagram for the selected controller layout
    this.layoutDiagram();
    this.prompts([['CROSS', 'Select'], ['CIRCLE', 'Back']]);
  }

  private optionRow(parent: HTMLElement, f: FieldDef): void {
    const row = el('div', 'b2-opt', parent);
    row.dataset['key'] = f.key;
    const lab = el('div', 'b2-opt-label', row);
    lab.appendChild(bm(f.label, 'Default', { upper: true }));
    const val = el('div', 'b2-opt-value', row);
    const cur = () => getPath(this.settings, f.key);
    switch (f.field.kind) {
      case 'bool': {
        const fk = f.field;
        const txt = bm('', 'Default');
        const left = el('span', 'b2-lr', val); left.textContent = '◀';
        val.appendChild(txt);
        const right = el('span', 'b2-lr', val); right.textContent = '▶';
        const render = () => setBm(txt, (cur() ? fk.on : fk.off) ?? '', true);
        const flip = () => {
          this.setSetting(f.key, !cur()); render(); this.layoutDiagram();
          // (integration, #H1.5) WebHID needs the chooser inside this user gesture (click / key)
          if (f.key === 'webHidRumble' && cur()) this.cb({ a: 'hidRumble' });
        };
        left.addEventListener('click', flip); right.addEventListener('click', flip);
        render();
        this.addFocus(row, flip, flip);
        break;
      }
      case 'choice': {
        const fk = f.field;
        const txt = bm('', 'Default');
        const left = el('span', 'b2-lr', val); left.textContent = '◀';
        val.appendChild(txt);
        const right = el('span', 'b2-lr', val); right.textContent = '▶';
        const idx = () => Math.max(0, fk.options.findIndex((o) => o.v === cur()));
        const render = () => setBm(txt, fk.options[idx()]!.label, true);
        const step = (d: number) => {
          const n = (idx() + d + fk.options.length) % fk.options.length;
          this.setSetting(f.key, fk.options[n]!.v);
          render();
          this.layoutDiagram();
        };
        left.addEventListener('click', () => step(-1)); right.addEventListener('click', () => step(1));
        render();
        this.addFocus(row, () => step(1), step);
        break;
      }
      case 'range': {
        const fk = f.field;
        const input = el('input', 'b2-slider', val);
        input.type = 'range';
        input.min = String(fk.min); input.max = String(fk.max); input.step = String(fk.step);
        input.value = String(cur());
        if (f.key === 'lookSensitivity') input.id = 'm-opt-sens';
        const num = bm('', 'Default', { className: 'b2-opt-num' });
        val.appendChild(num);
        const render = () => setBm(num, fk.fmt ? fk.fmt(Number(cur())) : String(cur()));
        const commit = (v: number) => {
          const snapped = Math.round((Math.min(fk.max, Math.max(fk.min, v)) - fk.min) / fk.step) * fk.step + fk.min;
          const clean = Number(snapped.toFixed(6));
          this.setSetting(f.key, clean);
          input.value = String(clean);
          render();
        };
        input.addEventListener('input', () => commit(Number(input.value)));
        render();
        this.addFocus(row, undefined, (d) => commit(Number(cur()) + d * fk.step));
        break;
      }
      case 'text': {
        const fk = f.field;
        const input = el('input', 'b2-input b2-text', val);
        input.maxLength = fk.maxLength;
        input.value = String(cur() ?? '');
        const commitText = () => this.setSetting(f.key, input.value.trim() || String(getPath(defaultSettings(), f.key) ?? ''));
        input.addEventListener('change', commitText);
        this.addFocus(input, undefined, undefined, { input, charset: OSK_NAME, maxLength: fk.maxLength, onDone: commitText });
        break;
      }
      case 'keybinds': {
        row.classList.add('b2-opt-keybinds');
        const grid = el('div', 'b2-keys', parent);
        const binds = this.settings.keybinds as Record<string, string[]>;
        for (const action of Object.keys(binds) as KbAction[]) {
          const kr = el('div', 'b2-key', grid);
          kr.appendChild(bm(action.replace(/([A-Z0-9])/g, ' $1').toUpperCase(), 'ExtraSmall'));
          const keyLab = bm('', 'ExtraSmall', { className: 'b2-keyval' });
          kr.appendChild(keyLab);
          const render = () => setBm(keyLab, (binds[action] ?? []).map(keyName).join(' / ') || '-', true);
          render();
          const start = () => {
            setBm(keyLab, 'PRESS A KEY (ESC CANCELS, BACKSPACE CLEARS)');
            kr.classList.add('listening');
            this.rebinding = { action, done: () => { kr.classList.remove('listening'); render(); } };
          };
          kr.addEventListener('click', start);
          this.addFocus(kr, start);
        }
        break;
      }
    }
  }

  /** Controller layout picker preview: what each DualSense button does in the chosen layout. */
  private layoutDiagram(): void {
    if (this.current !== 'options') return;
    this.stage.querySelector('.b2-layout')?.remove();
    const s = this.settings;
    const box = place(el('div', 'b2-layout', this.stage), 1280 - 64 - 380, 160, 380);
    box.appendChild(bm(`${STR.MENU_BUTTON_LAYOUT_CAPS}: ${String(s.buttonLayout).toUpperCase()}`, 'Condensed'));
    const table = BUTTON_LAYOUT_TABLE[s.buttonLayout] ?? BUTTON_LAYOUT_TABLE['default']!;
    const order = ['L2', 'R2', 'L1', 'R1', 'L3', 'R3', 'TRIANGLE', 'CIRCLE', 'SQUARE', 'CROSS'];
    for (const b of order) {
      let src = b;
      if (s.triggersFlipped) src = ({ L2: 'L1', L1: 'L2', R2: 'R1', R1: 'R2' } as Record<string, string>)[b] ?? b;
      const r = el('div', 'b2-layout-row', box);
      el('span', 'b2-glyph', r).textContent = PS_BUTTONS[b] ?? b;
      r.appendChild(bm(COMMAND_LABELS[table[src] ?? ''] ?? '-', 'ExtraSmall'));
    }
    const st = el('div', 'b2-layout-stick', box);
    st.appendChild(bm(`${STR.MENU_STICK_LAYOUT_CAPS}: ${(STICK_LAYOUT_TABLE[s.stickLayout]?.desc ?? '')}`, 'ExtraSmall'));
    const sens = el('div', 'b2-layout-stick', box);
    sens.appendChild(bm(`${STR.MENU_LOOK_SENSITIVITY_CAPS}: ${s.lookSensitivity}  (x${SENS_TABLE[s.lookSensitivity - 1] ?? 1})`, 'ExtraSmall'));
  }

  private setSetting(key: string, v: unknown): void {
    setPath(this.settings as unknown as Record<string, unknown>, key, v);
    this.cb({ a: 'settings', settings: structuredClone(this.settings) });
  }

  /** End of match: wager result list (after-action report style). */
  private showEnd(data: unknown): void {
    const d = data as ServerControl | { results?: ScoreRow[]; winner?: number | null } | undefined;
    if (d && typeof d === 'object' && 'results' in d && Array.isArray(d.results)) this.endData = { results: d.results, winner: d.winner ?? null };
    this.backdrop('front');
    const snap = matchStore.snap;
    const rows = sortPlacement(this.endData?.results?.length ? this.endData.results : snap.rows);
    this.endData = null;
    const places = placesOf(rows);
    const pay = wagerPayouts(rows, WAGER_DISPLAY_ANTE * rows.length);
    const mine = pay.get(snap.localClientNum) ?? 0;
    const head = place(el('div', `b2-end-title ${mine > 0 ? 'money' : 'loss'}`, this.stage), 64 + 6, 50);
    head.appendChild(bm(mine > 0 ? STR.MP_WAGER_IN_THE_MONEY_CAPS : STR.MP_WAGER_LOSS_CAPS, 'Morris'));
    place(el('div', 'b2-subtitle', this.stage), 64 + 8, 120).appendChild(bm(`${STR.MP_GAME_OVER_CAPS} - ${STR.MP_TIME_LIMIT_REACHED}`, 'Condensed', { upper: true }));
    const table = place(el('div', 'b2-end-table', this.stage), 64 + 6, 170, 760);
    const hdr = el('div', 'b2-end-row hdr', table);
    for (const h of ['', '', STR.MPUI_SCORE, STR.MPUI_KILLS, STR.MPUI_DEATHS, STR.MPUI_TOMAHAWKS, STR.MPUI_HUMILIATED, STR.MPUI_WINNINGS_CAPS]) {
      const c = el('span', 'c', hdr);
      if (h) c.appendChild(bm(h.toUpperCase(), 'ExtraSmall'));
    }
    for (const r of rows) {
      const pl = places.get(r.clientNum) ?? 0;
      const row = el('div', `b2-end-row${r.clientNum === snap.localClientNum ? ' me' : ''}${(pay.get(r.clientNum) ?? 0) > 0 ? ' money' : ''}`, table);
      const pc = el('span', 'c place', row);
      if (pl >= 1 && pl <= 3) img(`wager/menu_mp_lobby_aar_wager0${pl}.png`, 'b2-placeart', pc);
      pc.appendChild(bm(ordinalCaps(pl), 'Condensed'));
      el('span', 'c name', row).appendChild(bm(snap.names.get(r.clientNum) ?? `Player ${r.clientNum}`, 'Default'));
      for (const v of [r.points, r.kills, r.deaths, r.tomahawks, r.humiliated]) el('span', 'c', row).appendChild(bm(String(v), 'Default'));
      const w = el('span', 'c win', row);
      img((pay.get(r.clientNum) ?? 0) > 0 ? 'wager/wager_green.png' : 'wager/wager_yellow.png', 'b2-coin', w);
      w.appendChild(bm(String(pay.get(r.clientNum) ?? 0), 'Default'));
    }
    this.buttonList(64 + 6, 720 - 36 - 90, [{ id: 'm-leave', label: STR.MENU_BACK_CAPS, act: () => this.cb({ a: 'leave' }) }]);
    this.backAction = () => this.cb({ a: 'leave' });
    this.prompts([['CROSS', 'Select']]);
  }

  // =========================================================================================
  // input
  // =========================================================================================
  private onKey(e: KeyboardEvent): void {
    if (this.rebinding) {
      e.preventDefault(); e.stopPropagation();
      const rb = this.rebinding;
      this.rebinding = null;
      if (e.code !== 'Escape') {
        const binds = this.settings.keybinds as Record<string, string[]>;
        binds[rb.action] = e.code === 'Backspace' ? [] : [e.code];
        this.setSetting('keybinds', binds);
      }
      rb.done();
      return;
    }
    if (this.current === 'none' || this.current === 'loading') return;
    const inInput = document.activeElement instanceof HTMLInputElement && document.activeElement.type !== 'range';
    switch (e.key) {
      case 'ArrowDown': if (!inInput || e.altKey) { this.focus(this.focusIdx + 1); e.preventDefault(); } break;
      case 'ArrowUp': if (!inInput || e.altKey) { this.focus(this.focusIdx - 1); e.preventDefault(); } break;
      case 'ArrowLeft': if (!inInput) { this.focusables[this.focusIdx]?.adjust?.(-1); e.preventDefault(); } break;
      case 'ArrowRight': if (!inInput) { this.focusables[this.focusIdx]?.adjust?.(1); e.preventDefault(); } break;
      case 'Enter': {
        const f = this.focusables[this.focusIdx];
        if (f?.activate && !(document.activeElement instanceof HTMLButtonElement)) { f.activate(); e.preventDefault(); }
        break;
      }
      case 'Escape':
        if (this.osk) { e.preventDefault(); e.stopPropagation(); this.closeOsk(true); break; }
        if (this.current === 'pause' || this.current === 'options' || this.current === 'lobby' || this.current === 'error') {
          if (this.backAction) { e.preventDefault(); e.stopPropagation(); this.backAction(); }
        }
        break;
      default: break;
    }
  }

  private onMouseRebind(e: MouseEvent): void {
    if (!this.rebinding) return;
    // the click that started listening also lands here: ignore clicks on the row itself
    if ((e.target as HTMLElement).closest?.('.b2-key.listening')) return;
    e.preventDefault(); e.stopPropagation();
    const rb = this.rebinding;
    this.rebinding = null;
    const binds = this.settings.keybinds as Record<string, string[]>;
    binds[rb.action] = [`Mouse${e.button}`];
    this.setSetting('keybinds', binds);
    rb.done();
  }

  private updatePadLoop(): void {
    const want = this.current !== 'none' && this.current !== 'loading' && typeof navigator.getGamepads === 'function';
    if (want && !this.padRaf) {
      const tick = (t: number) => {
        this.padRaf = 0;
        if (this.current === 'none' || this.current === 'loading') return;
        this.pollPad(t);
        this.padRaf = requestAnimationFrame(tick);
      };
      this.padRaf = requestAnimationFrame(tick);
    }
  }

  /**
   * The menu pad: chosen like the input layer does (pad.ts pickPad: keep the active pad, switch to one that
   * produces input, else prefer a DualSense) and read with its mapping (readPad, raw-HID fallback [CTL §8.1]).
   */
  private readMenuPad(): boolean[] | null {
    if (typeof navigator.getGamepads !== 'function') return null;
    const list = [...navigator.getGamepads()] as (GamepadLike | null)[];
    const raw = (i: number) => { let r = this.padRaw.get(i); if (!r) { r = createPadRawState(); this.padRaw.set(i, r); } return r; };
    this.padIdx = pickPad(list, this.padIdx, (gp) => padHasInput(readPad(gp, this.padSample, raw(gp.index))));
    const gp = list.find((g) => g && g.connected && g.index === this.padIdx);
    if (!gp) return null;
    return menuPadButtons(readPad(gp, this.padSample, raw(gp.index)));
  }

  private pollPad(t: number): void {
    this.gesturePrompt();
    const now = this.readMenuPad();
    if (!now) { this.padPrev = []; return; }
    const prev = this.padPrev;
    const edge = (i: number) => now[i] && !prev[i];
    const held = now[MP.UP] || now[MP.DOWN] || now[MP.LEFT] || now[MP.RIGHT];
    const repeat = held && t >= this.padRepeatAt;
    const dirs = [MP.UP, MP.DOWN, MP.LEFT, MP.RIGHT];
    const go = (i: number) => edge(i) || (repeat && now[i]);
    this.padPrev = now;
    if (dirs.some((i) => edge(i))) this.padRepeatAt = t + 400;
    else if (repeat) this.padRepeatAt = t + 120;
    if (this.osk) {
      // on-screen keyboard: D-pad moves in the grid, Cross types, Square deletes, Circle / Options close
      if (go(MP.UP)) this.oskMove(0, -1);
      if (go(MP.DOWN)) this.oskMove(0, 1);
      if (go(MP.LEFT)) this.oskMove(-1, 0);
      if (go(MP.RIGHT)) this.oskMove(1, 0);
      if (edge(MP.SELECT)) this.oskPress();
      if (edge(MP.SQUARE)) this.oskType('DEL');
      if (edge(MP.BACK) || edge(MP.OPTIONS)) this.closeOsk(true);
      return;
    }
    const f = this.focusables[this.focusIdx];
    if (edge(MP.SELECT)) { if (f?.osk) this.openOsk(f.osk); else f?.activate?.(); }
    if (edge(MP.BACK) && this.backAction) this.backAction();
    if (edge(MP.OPTIONS) && this.current === 'pause') this.cb({ a: 'resume' });
    if (go(MP.UP)) this.focus(this.focusIdx - 1);
    if (go(MP.DOWN)) this.focus(this.focusIdx + 1);
    if (go(MP.LEFT)) this.focusables[this.focusIdx]?.adjust?.(-1);
    if (go(MP.RIGHT)) this.focusables[this.focusIdx]?.adjust?.(1);
  }

  // ---- on-screen keyboard (controller text entry) -------------------------------------------
  private openOsk(spec: OskSpec): void {
    this.closeOsk(false);
    const root = place(el('div', 'b2-osk', this.stage), this.vw / 2 - 300, 230, 600);
    root.id = 'm-osk';
    const shown = el('div', 'b2-osk-value', root);
    const grid = el('div', 'b2-osk-grid', root);
    const chars = [...spec.charset].map((c) => (c === ' ' ? 'SPACE' : c));
    const keys: { el: HTMLElement; ch: string }[] = [];
    for (const ch of [...chars, 'DEL', 'DONE']) {
      const k = el('button', `b2-osk-key${ch.length > 1 ? ' wide' : ''}`, grid);
      k.type = 'button';
      k.textContent = ch;
      k.dataset['ch'] = ch;
      const i = keys.length;
      k.addEventListener('click', () => { if (this.osk) { this.osk.idx = i; this.oskPress(); } });
      keys.push({ el: k, ch });
    }
    const hint = el('div', 'b2-osk-hint', root);
    hint.textContent = '✕ type   □ delete   ○ done';
    this.osk = { spec, keys, idx: 0, root, prevFocus: this.focusIdx };
    shown.textContent = spec.input.value || ' ';
    this.oskMove(0, 0);
  }

  private closeOsk(commit: boolean): void {
    const o = this.osk;
    if (!o) return;
    this.osk = null;
    o.root.remove();
    if (commit) {
      o.spec.input.dispatchEvent(new Event('change'));
      o.spec.onDone?.();
    }
  }

  private oskMove(dx: number, dy: number): void {
    const o = this.osk;
    if (!o) return;
    const n = o.keys.length;
    let i = o.idx;
    if (dx) i = (i + dx + n) % n;
    if (dy) { i += dy * OSK_COLS; if (i < 0) i = Math.min(n - 1, i + Math.ceil(n / OSK_COLS) * OSK_COLS); if (i >= n) i = i % OSK_COLS; }
    o.keys[o.idx]?.el.classList.remove('focus');
    o.idx = i;
    o.keys[i]!.el.classList.add('focus');
  }

  private oskPress(): void {
    const o = this.osk;
    if (!o) return;
    this.oskType(o.keys[o.idx]!.ch);
  }

  private oskType(ch: string): void {
    const o = this.osk;
    if (!o) return;
    const inp = o.spec.input;
    if (ch === 'DONE') { this.closeOsk(true); return; }
    if (ch === 'DEL') inp.value = inp.value.slice(0, -1);
    else if (inp.value.length < o.spec.maxLength) inp.value += ch === 'SPACE' ? ' ' : ch;
    inp.dispatchEvent(new Event('input'));
    const shown = o.root.querySelector('.b2-osk-value');
    if (shown) shown.textContent = inp.type === 'password' ? '•'.repeat(inp.value.length) || ' ' : inp.value || ' ';
  }

  // ---- "Press ✕ to connect" (browsers expose a pad only after a button press [CTL §8.1]) ------------
  private gesturePrompt(): void {
    const supported = typeof navigator.getGamepads === 'function';
    const anyPad = supported && [...navigator.getGamepads()].some((g) => g && g.connected);
    const need = supported && !anyPad && (this.current === 'main' || this.current === 'lobby' || this.current === 'error');
    if (need && !this.gestureEl) {
      const p = place(el('div', 'b2-pad-prompt', this.stage), 1280 - 64 - 420, 720 - 36 - 30);
      p.id = 'm-pad-prompt';
      p.appendChild(bm('PRESS', 'ExtraSmall'));
      el('span', 'b2-glyph g-CROSS', p).textContent = '✕';
      p.appendChild(bm('ON YOUR CONTROLLER TO CONNECT IT', 'ExtraSmall'));
      this.gestureEl = p;
    } else if (!need && this.gestureEl) {
      this.gestureEl.remove();
      this.gestureEl = null;
    }
  }
}

/** Human key names for the keybind list. */
export function keyName(code: string): string {
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Mouse')) return ['MOUSE1', 'MOUSE3', 'MOUSE2', 'MOUSE4', 'MOUSE5'][Number(code.slice(5))] ?? code;
  const m: Record<string, string> = {
    Space: 'SPACE', ShiftLeft: 'SHIFT', ShiftRight: 'RSHIFT', ControlLeft: 'CTRL', ControlRight: 'RCTRL', AltLeft: 'ALT',
    Escape: 'ESC', Tab: 'TAB', WheelUp: 'MWHEELUP', WheelDown: 'MWHEELDOWN', Enter: 'ENTER', Backquote: '`',
  };
  return m[code] ?? code.toUpperCase();
}
